import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { CalendarPlus, Check, DatabaseBackup, KeyRound, LogOut, Pencil, UserPlus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { NextUp } from "@/components/roster/NextUp";
import { RosterGrid } from "@/components/roster/RosterGrid";
import { PinDialog } from "@/components/roster/PinDialog";
import { PlayerDialog } from "@/components/roster/PlayerDialog";
import { WeekDialog } from "@/components/roster/WeekDialog";
import { BackupDialog } from "@/components/roster/BackupDialog";
import { PlayerSheet } from "@/components/roster/PlayerSheet";

const TOKEN_KEY = "captainAuth";
const POLL_INTERVAL_MS = 20000;
const STATUS_UNAUTHORIZED = 401;
const NEXT_STATUS = { "": "available", available: "maybe", maybe: "out", out: "" };

function loadToken() {
  try {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.token || new Date(parsed.expiresAt).getTime() <= Date.now()) return null;
    return parsed.token;
  } catch {
    return null;
  }
}

export default function App() {
  const [data, setData] = useState(null);
  const [token, setToken] = useState(loadToken);
  const [busy, setBusy] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [playerDialog, setPlayerDialog] = useState(null); // null | {player?}
  const [playerSheet, setPlayerSheet] = useState(null);
  const [weekDialog, setWeekDialog] = useState(null); // null | {week?}
  const [backupOpen, setBackupOpen] = useState(false);
  const [backupJson, setBackupJson] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  const refresh = useCallback(async () => {
    try {
      setData(await api.state());
    } catch (e) {
      console.error("Failed to refresh roster state", e);
    }
  }, []);

  useEffect(() => {
    document.title = "Team Availability Roster";
    refresh();
    const t = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  const handleAuthError = useCallback((e) => {
    if (e?.response?.status === STATUS_UNAUTHORIZED) {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken(null);
      toast.error("Admin session expired. Enter the PIN again.");
      return true;
    }
    return false;
  }, []);

  const setStatus = async (playerId, weekId, status) => {
    if (!data) return;
    const key = `${playerId}:${weekId}`;
    setData((prev) => {
      const availability = { ...prev.availability };
      if (status) availability[key] = status;
      else delete availability[key];
      return { ...prev, availability };
    });
    try {
      setData(await api.setAvailability(playerId, weekId, status, token));
    } catch (e) {
      if (!handleAuthError(e)) toast.error(errMsg(e));
      refresh();
    }
  };

  const onTapCell = (playerId, weekId) => {
    if (!data) return;
    const current = data.availability[`${playerId}:${weekId}`] || "";
    setStatus(playerId, weekId, NEXT_STATUS[current]);
  };

  const submitPin = async (pin) => {
    setBusy(true);
    try {
      const res = await api.verifyPin(pin);
      sessionStorage.setItem(TOKEN_KEY, JSON.stringify({ token: res.token, expiresAt: res.expiresAt }));
      setToken(res.token);
      setPinOpen(false);
      toast.success("Admin mode unlocked");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const savePlayer = async (name) => {
    setBusy(true);
    try {
      const editing = playerDialog?.player;
      setData(editing ? await api.editPlayer(editing.id, name, token) : await api.addPlayer(name, token));
      setPlayerDialog(null);
      toast.success(editing ? "Player updated" : "Player added");
    } catch (e) {
      if (!handleAuthError(e)) toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const saveWeek = async (form) => {
    setBusy(true);
    try {
      const editing = weekDialog?.week;
      setData(editing ? await api.editWeek(editing.id, form, token) : await api.addWeek(form, token));
      setWeekDialog(null);
      toast.success(editing ? "Week updated" : "Week added");
    } catch (e) {
      if (!handleAuthError(e)) toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const saveTeamName = async () => {
    if (!nameDraft.trim()) return;
    try {
      setData(await api.setTeamName(nameDraft, token));
      setEditingName(false);
      toast.success("Team name saved");
    } catch (e) {
      if (!handleAuthError(e)) toast.error(errMsg(e));
    }
  };

  const openBackup = async () => {
    setBackupOpen(true);
    setBackupJson("");
    try {
      setBackupJson(JSON.stringify(await api.backup(token), null, 2));
    } catch (e) {
      if (!handleAuthError(e)) toast.error(errMsg(e));
    }
  };

  const doRestore = async (text) => {
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      toast.error("Invalid JSON");
      return;
    }
    setBusy(true);
    try {
      setData(await api.restore(parsed, token));
      setBackupOpen(false);
      toast.success("Backup restored");
    } catch (e) {
      if (!handleAuthError(e)) toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const exitCaptain = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    toast.success("Admin mode locked");
  };

  const isCaptain = Boolean(token);

  return (
    <div className="min-h-screen bg-[#f7f5f0] text-slate-900">
      <Toaster theme="light" position="top-center" richColors />
      <div className="mx-auto max-w-6xl px-3 pb-10 pt-5 sm:px-5">
        <header className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-orange-400">Availability Roster</p>
            {editingName ? (
              <form
                className="mt-1 flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  saveTeamName();
                }}
              >
                <input
                  data-testid="team-name-input"
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  className="rounded-md border border-orange-400/50 bg-white px-2 py-1 text-base text-slate-900 outline-none"
                />
                <button data-testid="team-name-save-button" type="submit" className="rounded-md bg-orange-500 p-1.5 text-white hover:bg-orange-400" aria-label="Save team name">
                  <Check className="h-4 w-4" />
                </button>
              </form>
            ) : (
              <h1 className="font-display mt-0.5 flex items-center gap-2 text-2xl uppercase leading-none sm:text-3xl" data-testid="team-name">
                {data?.teamName || "…"}
                {isCaptain && (
                  <button
                    data-testid="team-name-edit-button"
                    onClick={() => {
                      setNameDraft(data?.teamName || "");
                      setEditingName(true);
                    }}
                    className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-orange-600"
                    aria-label="Edit team name"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                )}
              </h1>
            )}
          </div>
          <button
            data-testid="captain-toggle-button"
            onClick={() => (isCaptain ? exitCaptain() : setPinOpen(true))}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition-colors ${
              isCaptain ? "bg-orange-500 text-white hover:bg-orange-400" : "border border-slate-200 bg-white/70 text-slate-400 hover:border-orange-300 hover:text-orange-600"
            }`}
          >
            {isCaptain ? <LogOut className="h-3 w-3" /> : <KeyRound className="h-3 w-3" />}
            {isCaptain ? "Exit admin" : "Admin"}
          </button>
        </header>

        {!data ? (
          <p className="py-20 text-center text-slate-500" data-testid="loading-state">Loading roster…</p>
        ) : (
          <>
            <NextUp weeks={data.weeks} players={data.players} availability={data.availability} />

            {isCaptain && (
              <div className="fade-up mt-4 flex flex-wrap gap-2" data-testid="captain-toolbar">
                <button
                  data-testid="add-player-button"
                  onClick={() => setPlayerDialog({})}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:border-orange-400 hover:text-orange-600"
                >
                  <UserPlus className="h-3.5 w-3.5" /> Add player
                </button>
                <button
                  data-testid="add-week-button"
                  onClick={() => setWeekDialog({})}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:border-orange-400 hover:text-orange-600"
                >
                  <CalendarPlus className="h-3.5 w-3.5" /> Add week
                </button>
                <button
                  data-testid="backup-button"
                  onClick={openBackup}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:border-orange-400 hover:text-orange-600"
                >
                  <DatabaseBackup className="h-3.5 w-3.5" /> Backup / restore
                </button>
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500" data-testid="legend">
              <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded bg-emerald-500" /> Available</span>
              <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded bg-amber-400" /> Maybe</span>
              <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded bg-rose-500" /> Out</span>
              <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded border border-slate-300 bg-white" /> Tap to cycle</span>
            </div>

            <div className="mt-3">
              <RosterGrid
                players={data.players}
                weeks={data.weeks}
                availability={data.availability}
                isCaptain={isCaptain}
                onTapCell={onTapCell}
                onOpenPlayer={setPlayerSheet}
                onEditWeek={(week) => setWeekDialog({ week })}
                onEditPlayer={(player) => setPlayerDialog({ player })}
              />
            </div>
            <p className="mt-3 text-[11px] text-slate-500">
              Weeks lock automatically once the game date &amp; time pass. The admin can still edit locked weeks to keep GP records accurate.
            </p>
          </>
        )}
      </div>

      <PinDialog open={pinOpen} pinSet={Boolean(data?.pinSet)} onClose={() => setPinOpen(false)} onSubmit={submitPin} busy={busy} />
      <PlayerDialog open={Boolean(playerDialog)} player={playerDialog?.player || null} onClose={() => setPlayerDialog(null)} onSave={savePlayer} busy={busy} />
      <WeekDialog open={Boolean(weekDialog)} week={weekDialog?.week || null} onClose={() => setWeekDialog(null)} onSave={saveWeek} busy={busy} />
      <BackupDialog open={backupOpen} backupJson={backupJson} onClose={() => setBackupOpen(false)} onRestore={doRestore} busy={busy} />
      <PlayerSheet player={playerSheet} weeks={data?.weeks || []} availability={data?.availability || {}} isCaptain={isCaptain} onClose={() => setPlayerSheet(null)} onSet={setStatus} />
    </div>
  );
}
